import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import { BlaCompany } from '@app/core/entity/bla-company';
import { Client } from '@app/core/entity/client';
import { getTranslationProvider } from '@app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from '@app/core/i18n/TranslatableComponent';
import { CloudCompanyService } from '@app/core/services/cloud-company.service';
import { translateColumnHeaders } from '../../table/misc/translate-column-header';
import { TableColumn } from '../../table/interfaces/table-column';

@Component({
  selector: 'app-connect-cloud-company-dialog',
  templateUrl: 'connect-cloud-company-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [getTranslationProvider('ConnectCloudCompanyDialogComponent')],
})
export class ConnectCloudCompanyDialogComponent extends TranslatableComponent {
  public cloudCompanies$: Observable<{
    loading: boolean;
    error?: boolean;
    data: BlaCompany[] | undefined | null;
  }>;
  public cloudCompanyColumns$: Observable<TableColumn[]>;

  @Input() client: Client;
  @Input()
  get visible(): boolean {
    return this._visible;
  }
  set visible(value: boolean) {
    this._visible = value;
    this.visibleChange.emit(value);

    if (value) {
      this.initLoadOfCloudCompanies();
    }
  }

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter();
  @Output() updated: EventEmitter<Client> = new EventEmitter();

  private columns = [
    { field: 'orgNumber', header: 'orgNumber', width: '135px' },
    { field: 'name', header: 'name' },
    { field: 'publicKey', header: 'cloudDatabaseKey' },
    { field: 'action', header: '', width: '120px' },
  ];

  private _visible = false;

  constructor(private cloudCompanyService: CloudCompanyService) {
    super();

    this.cloudCompanyColumns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns));
  }

  public connectCloudCompany(cloudCompany: BlaCompany) {
    this.updated.emit({ ...this.client, cloudApiKey: cloudCompany.publicKey });
    this.closeDialog();
  }

  public closeDialog() {
    this.visible = false;
  }

  private initLoadOfCloudCompanies() {
    const filterMatchingCompaniesFn = this.findMatchingCompanies(this.client.corporateIdentity);
    const source = this.cloudCompanyService.getCloudCompanies(false, true).pipe(
      map(filterMatchingCompaniesFn),
      map((cloudCompanies: BlaCompany[]) => ({ loading: false, data: cloudCompanies })),
      catchError(() => of({ loading: false, error: true, data: null })),
    );

    this.cloudCompanies$ = source.pipe(startWith({ loading: true, data: null }));
  }

  private findMatchingCompanies(clientCorporateIdentity: string) {
    return (cloudCompanies: BlaCompany[]) =>
      (cloudCompanies || []).filter((cc) => !!cc.publicKey && cc.orgNumber === clientCorporateIdentity);
  }
}
